import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatMoney, formatWritten } from "@/lib/format";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Users2, FilePlus2, History, FileText, PoundSterling,
  FileStack, ArrowRight, Loader2,
} from "lucide-react";

const statusBadge = {
  draft: "bg-slate-100 text-slate-700",
  finalised: "bg-emerald-100 text-emerald-700",
  superseded: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cass-blue" /></div>;
  }

  const stats = [
    { label: `Total dividends FY ${data.fy_year}`, value: formatMoney(data.fy_total), icon: PoundSterling },
    { label: "Vouchers generated", value: data.voucher_count, icon: FileStack },
    { label: "Companies", value: data.companies_count, icon: Building2 },
    { label: "Active shareholders", value: data.shareholders_count, icon: Users2 },
  ];

  const quickLinks = [
    { to: "/companies", label: "Companies", icon: Building2 },
    { to: "/shareholders", label: "Shareholders", icon: Users2 },
    { to: "/create-dividend", label: "Create Dividend", icon: FilePlus2 },
    { to: "/history", label: "Dividend History", icon: History },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" description="Operational overview of dividend activity.">
        <Button onClick={() => navigate("/create-dividend")} data-testid="dashboard-create-dividend"
          className="bg-cass-blue text-white hover:bg-cass-bluehover">
          <FilePlus2 className="mr-2 h-4 w-4" /> Create Dividend
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-cass-border p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-cass-navy/50">{s.label}</span>
              <s.icon className="h-4 w-4 text-cass-blue" />
            </div>
            <div className="mt-3 font-head text-3xl font-bold tabular text-cass-navy">{s.value}</div>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-cass-border p-6 shadow-sm">
          <h3 className="mb-4 font-head text-lg font-semibold text-cass-navy">Drafts awaiting completion</h3>
          {data.drafts.length === 0 ? (
            <p className="text-sm text-cass-navy/50">No draft dividends.</p>
          ) : (
            <div className="space-y-2">
              {data.drafts.map((d) => (
                <button key={d.id} onClick={() => navigate(`/dividends/${d.id}`)}
                  data-testid={`draft-${d.id}`}
                  className="flex w-full items-center justify-between rounded-md border border-cass-border p-3 text-left transition-colors hover:bg-cass-surface">
                  <div>
                    <div className="text-sm font-medium text-cass-navy">{d.period_label || "Dividend"}</div>
                    <div className="text-xs text-cass-navy/50">{d.share_class} · {(d.lines || []).length} shareholder(s)</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="tabular text-sm font-semibold text-cass-navy">{formatMoney(d.grand_total)}</span>
                    <ArrowRight className="h-4 w-4 text-cass-blue" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="border-cass-border p-6 shadow-sm">
          <h3 className="mb-4 font-head text-lg font-semibold text-cass-navy">Recently generated</h3>
          {data.recent.length === 0 ? (
            <p className="text-sm text-cass-navy/50">Nothing yet.</p>
          ) : (
            <div className="space-y-2">
              {data.recent.map((d) => (
                <button key={d.id} onClick={() => navigate(`/dividends/${d.id}`)}
                  data-testid={`recent-${d.id}`}
                  className="flex w-full items-center justify-between rounded-md border border-cass-border p-3 text-left transition-colors hover:bg-cass-surface">
                  <div>
                    <div className="text-sm font-medium text-cass-navy">{d.period_label || "Dividend"}</div>
                    <div className="text-xs text-cass-navy/50">{formatWritten(d.period_end)}</div>
                  </div>
                  <Badge className={`${statusBadge[d.status]} border-0 capitalize`}>{d.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <h3 className="mb-4 font-head text-lg font-semibold text-cass-navy">Quick links</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {quickLinks.map((q) => (
            <button key={q.to} onClick={() => navigate(q.to)} data-testid={`quicklink-${q.label.toLowerCase().replace(/ /g, "-")}`}
              className="flex flex-col items-start gap-3 rounded-lg border border-cass-border bg-white p-5 text-left transition-colors hover:border-cass-blue hover:bg-cass-surface">
              <q.icon className="h-5 w-5 text-cass-blue" />
              <span className="text-sm font-medium text-cass-navy">{q.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
